import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { C, TRADES, ALL_TRADES } from "../lib/constants";
import { validateImageFile } from "../lib/helpers";
import { uploadPublicFile } from "../lib/storage";
import { upsertDirectoryWorkerCache } from "../lib/cache";
import { Btn } from "./ui/Btn";
import { Inp } from "./ui/Inp";
import { TopNav } from "./ui/TopNav";
import { Av } from "./ui/Av";
import { Card } from "./ui/Card";
import { FileUploadCard, PhotoGrid } from "./ui/FileUpload";

const saveWorkerProfileInTable = async (table, payload, userId) => {
  const tablePayload = Object.fromEntries(
    Object.entries(payload || {}).filter(([, value]) => value !== undefined)
  );

  if (userId) {
    // PASO 38: usa upsert directo por user_id en lugar de select + insert/update separados.
    // Elimina race conditions y duplicados cuando el trabajador se registra dos veces.
    const upserted = await supabase
      .from(table)
      .upsert([{ ...tablePayload, user_id: userId }], { onConflict: "user_id" })
      .select("*")
      .maybeSingle();
    if (upserted.error) throw upserted.error;
    return upserted.data;
  }

  const inserted = await supabase
    .from(table)
    .insert([tablePayload])
    .select("*")
    .maybeSingle();
  if (inserted.error) throw inserted.error;
  return inserted.data;
};

const Sel = ({label, value, onChange, options}) => (
  <div style={{marginBottom:16}}>
    {label&&<div style={{color:C.sub,fontSize:11,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",marginBottom:6}}>{label}</div>}
    <select value={value} onChange={onChange} style={{width:"100%",background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:12,padding:"13px 16px",color:C.text,fontSize:14,outline:"none",cursor:"pointer",fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif"}}>
      {options.map(o=><option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export const RegWorker = ({setScreen, user, refreshDirectory}) => {
  const [step,          setStep]          = useState(1);
  const [saving,        setSaving]        = useState(false);
  const [done,          setDone]          = useState(false);
  const [errorMsg,      setErrorMsg]      = useState("");
  const [validationMsg, setValidationMsg] = useState("");
  const [uploadProgress,setUploadProgress]= useState("");
  const [perfilCargado, setPerfilCargado] = useState(false);
  const [precargando,   setPrecargando]   = useState(true);

  const [form, setForm] = useState({
    name:"", phone:"", trade:"Albañil", specialty:"", zone:"Los Mochis", exp:"",
    // Campos de acceso — solo obligatorios si el usuario NO está autenticado
    email:"", password:"", confirmPassword:"",
    ine:null, photo:null, portfolioPhotos:[],
    ine_url_existente:"", photo_url_existente:"",
    plan:"basico", metodo_pago:"",
    acepta_politicas:false, acepta_fondo:false, acepta_visibilidad:false, acepta_reglamento:false,
    activacion_anticipada:false,
  });

  const upd = (k,v) => { setValidationMsg(""); setErrorMsg(""); setForm(f=>({...f,[k]:v})); };

  useEffect(() => {
    if (perfilCargado) return;
    if (!user?.id) { setPrecargando(false); setPerfilCargado(true); return; }
    const precargar = async () => {
      try {
        // FIX SESIÓN CRUZADA: verificar que la sesión activa coincide antes de precargar datos
        const { data: authCheck } = await supabase.auth.getUser();
        const currentUid = authCheck?.user?.id;
        if (!currentUid || currentUid !== user.id) {
          console.warn("FICIO RegWorker: discrepancia de sesión — mostrando formulario vacío");
          setPrecargando(false);
          setPerfilCargado(true);
          return;
        }

        const [resP, resC, resW] = await Promise.all([
          supabase.from("perfiles").select("nombre,telefono,foto_url,ine_url").eq("user_id",currentUid).neq("archivado",true).maybeSingle(),
          supabase.from("clientes").select("nombre,telefono,correo,foto_url,ine_url,avatar").eq("user_id",currentUid).maybeSingle(),
          supabase.from("trabajadores").select("nombre,telefono,oficio,especialidad,zona,exp,foto_url,ine_url,avatar").eq("user_id",currentUid).neq("archivado",true).maybeSingle(),
        ]);
        const p = resP.data || {};
        const c = resC.data || {};
        const w = resW.data || {};
        // Shared identity from perfiles only — never from trabajadores, clientes, or user_metadata
        const nombre  = p.nombre   || "";
        const tel     = p.telefono || "";
        const fotoUrl = w.foto_url || w.avatar || p.foto_url || c.foto_url || c.avatar || "";
        const ineUrl  = w.ine_url  || p.ine_url || c.ine_url || "";
        setForm(f => ({
          ...f,
          name:  nombre,
          phone: String(tel).replace(/\D/g,""),
          trade:      w.oficio       || f.trade,
          specialty:  w.especialidad || f.specialty,
          zone:       w.zona         || f.zone,
          exp:        String(w.exp   || ""),
          email: user?.email || c.correo || c.email || f.email,
          photo_url_existente: fotoUrl,
          ine_url_existente:   ineUrl,
          photo: fotoUrl || null,
          ine:   ineUrl  || null,
        }));
      } catch(e) {
        console.warn("Precarga perfil:", e.message);
        // Leave form blank on error — never fall back to user_metadata
      } finally {
        setPerfilCargado(true);
        setPrecargando(false);
      }
    };
    precargar();
  }, [user?.id]); // eslint-disable-line

  const PLANS = [
    { id:"basico",  label:"Básico",   price:"$109/mes", desc:"Directorio + Fondo hasta $3,000",           color:C.accent },
    { id:"premium", label:"Premium",  price:"$219/mes", desc:"Posición destacada + Fondo hasta $5,000",    color:C.blue },
  ];

  // ── Validaciones por paso ────────────────────────────────────────────────────
  const validarPaso = (siguiente) => {
    setValidationMsg("");
    if (siguiente === 2) {
      if (!form.name.trim())  return setValidationMsg("Escribe tu nombre completo.");
      if (!form.phone.trim()) return setValidationMsg("Escribe tu número de WhatsApp.");
      if (!form.trade)        return setValidationMsg("Selecciona tu oficio principal.");
      if (!form.zone)         return setValidationMsg("Selecciona tu ciudad.");
      // Validar correo y contraseña SOLO si el usuario NO está autenticado
      if (!user?.id) {
        const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
        if (!form.email.trim())   return setValidationMsg("Escribe tu correo electrónico.");
        if (!emailValido)         return setValidationMsg("El correo no tiene un formato válido.");
        if (!form.password)       return setValidationMsg("Crea una contraseña para tu cuenta.");
        if (form.password.length < 6) return setValidationMsg("La contraseña debe tener al menos 6 caracteres.");
        if (form.password !== form.confirmPassword) return setValidationMsg("Las contraseñas no coinciden. Verifica.");
      }
    }
    if (siguiente === 3) {
      // Acepta File nuevo O string URL existente (precargada del perfil de cliente)
      const ineOk   = form.ine   instanceof File || (typeof form.ine   === "string" && form.ine.length   > 0);
      const photoOk = form.photo instanceof File || (typeof form.photo === "string" && form.photo.length > 0);
      if (!ineOk)   return setValidationMsg("La foto de tu INE es obligatoria.");
      if (!photoOk) return setValidationMsg("Tu foto de perfil es obligatoria.");
      const fotosValidas = form.portfolioPhotos.filter(Boolean);
      if (fotosValidas.length < 2) return setValidationMsg("Sube al menos 2 fotos de trabajos anteriores.");
    }
    if (siguiente === "guardar") {
      // FICIO: metodo_pago eliminado en v1 — membresías se activan manualmente vía admin/WhatsApp.
      if (!form.acepta_politicas)    return setValidationMsg("Debes leer y aceptar el reglamento completo de Ficio.");
      if (!form.acepta_fondo)        return setValidationMsg("Debes aceptar los términos del Fondo de Contingencia.");
      if (!form.acepta_visibilidad)  return setValidationMsg("Debes aceptar la política de visibilidad y membresía.");
    }
    return true;
  };

  const goStep = (n) => {
    if (!validarPaso(n)) return;
    setStep(n);
    try { window.scrollTo({top:0, behavior:"smooth"}); } catch {}
  };

  // ── Guardar trabajador ───────────────────────────────────────────────────────
  const guardarTrabajador = async () => {
    if (!validarPaso("guardar")) return;
    setSaving(true);
    setErrorMsg("");

    try {
      // ── Paso 1: Obtener o crear cuenta en Supabase Auth ───────────────────────
      let userId;
      let userEmail;

      const { data: authData } = await supabase.auth.getUser();
      const yaLogueado = Boolean(authData?.user?.id);

      if (yaLogueado) {
        // Usuario ya autenticado — usar la sesión activa
        userId    = authData.user.id;
        userEmail = authData.user.email || form.email.trim();
      } else {
        // Usuario NO autenticado — crear cuenta con email + password
        const emailLimpio = form.email.trim().toLowerCase();
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email:    emailLimpio,
          password: form.password,
          options: {
            data: {
              nombre:   form.name.trim(),
              telefono: form.phone.replace(/\D/g, ""),
              role:     "trabajador",
            }
          }
        });
        if (signUpError) {
          const isAlreadyReg = signUpError.message?.includes("already registered") || signUpError.message?.includes("User already registered");
          if (!isAlreadyReg) throw new Error("No se pudo crear tu cuenta: " + signUpError.message);
          // Email exists in Auth — sign in to verify whether a FICIO profile row exists
          const { data: siData, error: siError } = await supabase.auth.signInWithPassword({ email: emailLimpio, password: form.password });
          if (siError) throw new Error("Este correo ya tiene una cuenta en FICIO. Inicia sesión primero.");
          const { data: perfilRow } = await supabase.from("perfiles").select("id").eq("user_id", siData.user?.id).maybeSingle();
          if (perfilRow) {
            await supabase.auth.signOut();
            throw new Error("Este correo ya tiene una cuenta registrada en FICIO. Inicia sesión primero.");
          }
          // Ghost user: Auth account exists but no FICIO profile — allow registration
          userId    = siData.user?.id;
          userEmail = emailLimpio;
        } else if (!signUpData?.user?.id) {
          throw new Error("Error al crear cuenta. Intenta con otro correo o revisa tu conexión.");
        } else {
          userId    = signUpData.user.id;
          userEmail = emailLimpio;
        }
      }

      const cleanName = form.name.trim();
      const cleanPhone= form.phone.replace(/\D/g,"");

      // 1. Subir imágenes a Supabase Storage
      let fotoUrl = "";
      let ineUrl  = "";
      const portfolioUrls = [];

      // FIX F3-16: validar tipo y tamaño antes de subir.
      const photoErr = validateImageFile(form.photo instanceof File ? form.photo : null, "Foto");
      const ineErr   = validateImageFile(form.ine   instanceof File ? form.ine   : null, "INE");
      if (photoErr) throw new Error(photoErr);
      if (ineErr)   throw new Error(ineErr);

      setUploadProgress("Subiendo foto de perfil...");
      if (form.photo instanceof File) {
        fotoUrl = await uploadPublicFile(form.photo, "fotos-perfil", userId + "-perfil");
      } else if (typeof form.photo === "string" && form.photo) {
        fotoUrl = form.photo; // URL ya existente del registro de cliente
      }

      setUploadProgress("Subiendo INE...");
      if (form.ine instanceof File) {
        ineUrl = await uploadPublicFile(form.ine, "documentos-identidad", userId + "-ine");
      } else if (typeof form.ine === "string" && form.ine) {
        ineUrl = form.ine; // URL ya existente del registro de cliente
      }

      setUploadProgress("Subiendo portafolio...");
      for (const [i, p] of form.portfolioPhotos.entries()) {
        if (p?.file instanceof File) {
          const url = await uploadPublicFile(p.file, "ficio-media", userId + "-portfolio-" + i);
          if (url) portfolioUrls.push(url);
        }
      }

      setUploadProgress("Guardando perfil...");

      const payload = {
        user_id:            userId,
        nombre:             cleanName,
        telefono:           cleanPhone,
        whatsapp:           cleanPhone,
        correo:             userEmail || form.email.trim() || "",
        email:              userEmail || form.email.trim() || "",
        oficio:             form.trade,
        especialidad:       form.specialty || "",
        descripcion:        form.specialty || "",
        zona:               form.zone,
        ciudad:             form.zone,
        exp:                Number(form.exp) || 0,
        experiencia:        String(form.exp || 0),
        foto_url:           fotoUrl,
        ine_url:            ineUrl,
        portfolio_urls:     portfolioUrls,
        disponible:         true,
        verificado:         false,
        visible_directorio: true,
        membresia_activa:   true,
        membresia:          "prueba", // FICIO: estado inicial — admin activa manualmente vía panel
        plan:               form.plan,
        // FICIO: metodo_pago fijo en v1 — membresías se gestionan manualmente por admin.
        metodo_pago:        "whatsapp",
        periodo_prueba_dias:60,
        fondo_activo_desde_dia: 61,
        fondo_contingencia: false,
        calificacion:       0,
        trabajos:           0,
        total_trabajos:     0,
        "reseñas":          0,
        nuevo:              true,
        updated_at:         new Date().toISOString(),
      };

      // FIX SESIÓN CRUZADA: validar antes de guardar que el user_id del payload
      // coincide con el usuario autenticado. Previene escritura cruzada de perfiles.
      if (!payload.user_id || payload.user_id !== userId) {
        throw new Error("Error de sesión: el perfil no pertenece a esta cuenta. Cierra sesión e intenta de nuevo.");
      }

      // 2. Guardar SOLO en trabajadores — fuente única, sin duplicados
      try {
        await saveWorkerProfileInTable("trabajadores", payload, userId);
      } catch(e) {
        throw new Error("No se pudo guardar el perfil: " + e.message);
      }

      // 2b. Sync shared identity fields to perfiles (only non-empty values)
      const perfilSync = Object.fromEntries(
        Object.entries({
          user_id:     userId,
          nombre:      cleanName            || undefined,
          telefono:    cleanPhone           || undefined,
          email:       userEmail            || undefined,
          correo:      userEmail            || undefined,
          foto_url:    fotoUrl              || undefined,
          avatar_url:  fotoUrl              || undefined,
          ine_url:     ineUrl               || undefined,
          modo_activo: "trabajador",
          role:        "trabajador",
          rol:         "trabajador",
          updated_at:  new Date().toISOString(),
        }).filter(([, v]) => v !== undefined)
      );
      try { await supabase.from("perfiles").upsert([perfilSync], { onConflict: "user_id" }); } catch(_) {}

      // 3. Actualizar caché y directorio
      upsertDirectoryWorkerCache(payload);
      if (typeof refreshDirectory === "function") await refreshDirectory({ force: true });

      // Mode is set via cargarPerfil reading perfiles.modo_activo from DB.

      setDone(true);

    } catch (err) {
      setErrorMsg(err?.message || "Error al guardar. Intenta de nuevo.");
    } finally {
      setSaving(false);
      setUploadProgress("");
    }
  };

  // ── Pantalla de éxito ────────────────────────────────────────────────────────
  if (done) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{maxWidth:480,width:"100%",textAlign:"center"}}>
        <div style={{fontSize:80,marginBottom:12}}>🎉</div>
        <div style={{fontFamily:"Georgia,serif",fontSize:28,fontWeight:900,color:C.text,marginBottom:8}}>¡Ya estás en Ficio!</div>
        <div style={{background:C.greenL,border:`1.5px solid ${C.green}30`,borderRadius:16,padding:18,marginBottom:20,textAlign:"left"}}>
          {[
            ["Perfil","En revisión — visible en menos de 24 horas"],
            ["Plan", PLANS.find(p=>p.id===form.plan)?.label || "Básico"],
            ["Oficio", form.trade],
            ["Ciudad", form.zone],
            ["Periodo de prueba","60 días gratis · fondo activo el día 61"],
          ].map(([k,v])=>(
            <div key={k} style={{display:"flex",justifyContent:"space-between",marginBottom:6,paddingBottom:6,borderBottom:`1px solid ${C.green}20`}}>
              <span style={{color:C.sub,fontSize:13}}>{k}</span>
              <span style={{color:C.text,fontSize:13,fontWeight:700}}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{color:C.sub,fontSize:13,marginBottom:24,lineHeight:1.6}}>
          El equipo Ficio revisará tu INE y fotos. Te avisamos por WhatsApp cuando tu perfil esté activo en el directorio.
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <Btn full color={C.accent} onClick={()=>setScreen("obra-activa")}>Ir a Mi Obra →</Btn>
          <Btn full outline color={C.accent} onClick={()=>setScreen("directory")}>Ver el directorio</Btn>
        </div>
      </div>
    </div>
  );

  // ── UI ───────────────────────────────────────────────────────────────────────
  return (
    <div style={{minHeight:"100vh",background:C.bg,paddingBottom:40}}>
      <TopNav
        setScreen={setScreen}
        back={step > 1 ? () => setStep(s => s-1) : "landing"}
        backLabel={step > 1 ? `Paso ${step-1}` : "Inicio"}
        title="Registro de Trabajador"
        right={
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            {[1,2,3].map(s => (
              <div key={s} style={{display:"flex",alignItems:"center",gap:4}}>
                <div style={{width:28,height:6,borderRadius:4,background:s<=step?C.accent:C.faint,transition:"background 0.3s"}}/>
              </div>
            ))}
          </div>
        }
      />

      <div style={{maxWidth:560,margin:"0 auto",padding:"28px 20px"}}>

        {/* Indicador de paso */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:"10px 16px",marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
          {[
            {n:1, label:"Datos", icon:"👤"},
            {n:2, label:"Fotos e INE", icon:"📸"},
            {n:3, label:"Membresía", icon:"🛡️"},
          ].map((s,i) => (
            <div key={s.n} style={{display:"flex",alignItems:"center",gap:i<2?6:0,flex:i<2?1:"auto"}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:step>=s.n?C.accent:C.faint,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:step>=s.n?"#fff":C.muted,flexShrink:0}}>
                  {step>s.n ? "✓" : s.n}
                </div>
                <span style={{fontSize:12,fontWeight:700,color:step>=s.n?C.text:C.muted}}>{s.label}</span>
              </div>
              {i<2 && <div style={{flex:1,height:2,background:step>s.n?C.accent:C.faint,borderRadius:2,margin:"0 6px"}}/>}
            </div>
          ))}
        </div>

        {validationMsg && (
          <div style={{background:C.redL,border:`1px solid ${C.red}30`,borderRadius:12,padding:"10px 14px",color:C.red,fontSize:13,marginBottom:16,fontWeight:700}}>
            ⚠️ {validationMsg}
          </div>
        )}

        {/* ══ PASO 1: DATOS ═══════════════════════════════════════════════════ */}
        {step === 1 && (
          <div>
            <div style={{marginBottom:22}}>
              <div style={{fontFamily:"Georgia,serif",fontSize:26,fontWeight:800,color:C.text,marginBottom:4}}>¿Quién eres?</div>
              <div style={{color:C.muted,fontSize:13}}>Esto es lo que verán los clientes cuando te busquen.</div>
            </div>

            <Inp label="Nombre completo" placeholder="Como aparece en tu INE" value={form.name} onChange={e=>upd("name",e.target.value)} icon="👤"/>
            <Inp label="WhatsApp" type="tel" placeholder="10 dígitos sin espacios" value={form.phone} onChange={e=>upd("phone",e.target.value)} icon="📱"/>
            <Sel label="Oficio principal" value={form.trade} onChange={e=>upd("trade",e.target.value)} options={ALL_TRADES}/>
            <Inp label="Especialidad" placeholder="Ej: Cimentaciones, azulejo, interiores..." value={form.specialty} onChange={e=>upd("specialty",e.target.value)} icon="🔧"/>
            <Sel label="Ciudad donde trabajas" value={form.zone} onChange={e=>upd("zone",e.target.value)} options={["Los Mochis","Culiacán","Mazatlán","Guasave","Guamúchil","El Fuerte","Ahome","Navolato"]}/>
            <Inp label="Años de experiencia" type="number" placeholder="Ej: 8" value={form.exp} onChange={e=>upd("exp",e.target.value)} icon="📅"/>

            {/* Acceso a cuenta — solo si el usuario NO está logueado */}
            {!user?.id && (
              <>
                <div style={{height:8}}/>
                <div style={{background:C.blueL,border:`1.5px solid ${C.blue}30`,borderRadius:14,padding:"14px 16px",marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                    <span style={{fontSize:16}}>🔐</span>
                    <div>
                      <div style={{color:C.blue,fontWeight:800,fontSize:13}}>Crea tu acceso a Ficio</div>
                      <div style={{color:C.sub,fontSize:11}}>Con este correo y contraseña podrás iniciar sesión después.</div>
                    </div>
                  </div>
                  <Inp label="Correo electrónico" type="email" placeholder="tu@correo.com" value={form.email} onChange={e=>upd("email",e.target.value)} icon="📧"/>
                  <Inp label="Contraseña" type="password" placeholder="Mínimo 6 caracteres" value={form.password} onChange={e=>upd("password",e.target.value)} icon="🔒"/>
                  <Inp label="Confirmar contraseña" type="password" placeholder="Repite la contraseña" value={form.confirmPassword} onChange={e=>upd("confirmPassword",e.target.value)} icon="🔒"/>
                  {form.password && form.confirmPassword && form.password !== form.confirmPassword && (
                    <div style={{color:C.red,fontSize:11,fontWeight:700,marginTop:4}}>⚠ Las contraseñas no coinciden</div>
                  )}
                  {form.password && form.password.length > 0 && form.password.length < 6 && (
                    <div style={{color:"#B06000",fontSize:11,fontWeight:700,marginTop:4}}>La contraseña debe tener al menos 6 caracteres</div>
                  )}
                  {form.password && form.password.length >= 6 && form.password === form.confirmPassword && (
                    <div style={{color:C.green,fontSize:11,fontWeight:700,marginTop:4}}>✓ Contraseña lista</div>
                  )}
                </div>
              </>
            )}

            {/* Si ya está logueado, mostrar confirmación de cuenta */}
            {user?.id && (
              <div style={{background:C.greenL,border:`1px solid ${C.green}40`,borderRadius:12,padding:"10px 14px",marginBottom:12,display:"flex",gap:10,alignItems:"center"}}>
                <span style={{fontSize:16}}>✅</span>
                <div>
                  <div style={{color:C.green,fontWeight:700,fontSize:12}}>Sesión activa</div>
                  <div style={{color:C.sub,fontSize:11}}>{user?.email || "Cuenta verificada"}</div>
                </div>
              </div>
            )}

            <Btn full color={C.accent} onClick={()=>goStep(2)}>Continuar → Fotos e INE</Btn>
          </div>
        )}

        {/* ══ PASO 2: DOCUMENTOS Y FOTOS ══════════════════════════════════════ */}
        {step === 2 && (
          <div>
            <div style={{marginBottom:22}}>
              <div style={{fontFamily:"Georgia,serif",fontSize:26,fontWeight:800,color:C.text,marginBottom:4}}>Fotos e INE</div>
              <div style={{color:C.muted,fontSize:13}}>Todo es obligatorio. Las fotos generan confianza en los clientes.</div>
            </div>


            {precargando ? (
              <div style={{textAlign:"center",padding:"32px 0",color:C.muted}}>
                <div style={{fontSize:32,marginBottom:8}}>⏳</div>
                <div style={{fontSize:13}}>Buscando tus fotos anteriores...</div>
              </div>
            ) : (
              <>
            {!form.ine_url_existente && !form.photo_url_existente && (
              <div style={{background:C.blueL,border:`1px solid ${C.blue}25`,borderRadius:12,padding:"12px 14px",marginBottom:16,display:"flex",gap:10,alignItems:"flex-start"}}>
                <span style={{fontSize:16,flexShrink:0}}>ℹ️</span>
                <div style={{color:C.blue,fontSize:12,lineHeight:1.5}}>No encontramos fotos de un registro anterior. Sube tu INE y foto de perfil.</div>
              </div>
            )}

            {/* INE + Foto perfil */}
            <div style={{color:C.sub,fontSize:11,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",marginBottom:10}}>Identidad</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
              <div>
                {form.ine_url_existente && typeof form.ine === "string" ? (
                  <div style={{border:`2px solid ${C.green}`,borderRadius:16,padding:14,textAlign:"center",background:C.greenL}}>
                    <div style={{fontSize:28,marginBottom:4}}>✅</div>
                    <div style={{color:C.green,fontWeight:700,fontSize:12}}>INE guardado</div>
                    <div style={{color:C.muted,fontSize:10,marginTop:2}}>Del registro anterior</div>
                    <button onClick={()=>upd("ine",null)} style={{background:"none",border:"none",color:C.accent,fontSize:11,cursor:"pointer",marginTop:6,fontWeight:700}}>Cambiar</button>
                  </div>
                ) : (
                  <FileUploadCard id="ficio-ine" value={form.ine} onChange={file=>upd("ine",file)} icon="🪪" title="Foto de INE" sub="Frente visible — obligatorio"/>
                )}
                {!form.ine && <div style={{color:C.red,fontSize:10,marginTop:4,textAlign:"center",fontWeight:700}}>Requerido</div>}
              </div>
              <div>
                {form.photo_url_existente && typeof form.photo === "string" ? (
                  <div style={{border:`2px solid ${C.green}`,borderRadius:16,padding:14,textAlign:"center",background:C.greenL}}>
                    {form.photo_url_existente.startsWith("http") && (
                      <img src={form.photo_url_existente} alt="Perfil" style={{width:52,height:52,borderRadius:"50%",objectFit:"cover",marginBottom:4}}/>
                    )}
                    <div style={{color:C.green,fontWeight:700,fontSize:12}}>Foto guardada</div>
                    <div style={{color:C.muted,fontSize:10,marginTop:2}}>Del registro anterior</div>
                    <button onClick={()=>upd("photo",null)} style={{background:"none",border:"none",color:C.accent,fontSize:11,cursor:"pointer",marginTop:6,fontWeight:700}}>Cambiar</button>
                  </div>
                ) : (
                  <FileUploadCard id="ficio-photo" value={form.photo} onChange={file=>upd("photo",file)} icon="📸" title="Tu foto" sub="Cara visible — obligatorio"/>
                )}
                {!form.photo && <div style={{color:C.red,fontSize:10,marginTop:4,textAlign:"center",fontWeight:700}}>Requerido</div>}
              </div>
            </div>

            {/* Portafolio */}
            <div style={{color:C.sub,fontSize:11,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",marginBottom:6}}>
              Fotos de trabajos anteriores
            </div>
            <div style={{background:C.accentL,border:`1px solid ${C.accent}25`,borderRadius:10,padding:"10px 14px",marginBottom:12,display:"flex",gap:8,alignItems:"center"}}>
              <span style={{fontSize:16}}>💡</span>
              <div style={{color:C.accent,fontSize:12,lineHeight:1.5}}>
                Mínimo 2 fotos, máximo 6. Muestra trabajos reales que hayas hecho — esto es lo que más convence a los clientes.
              </div>
            </div>

            <PhotoGrid form={form} upd={upd}/>

            <div style={{marginTop:6,marginBottom:20}}>
              <div style={{color:C.muted,fontSize:11,textAlign:"right"}}>
                {form.portfolioPhotos.filter(Boolean).length}/6 fotos subidas
                {form.portfolioPhotos.filter(Boolean).length < 2 && (
                  <span style={{color:C.red,fontWeight:700}}> · mínimo 2</span>
                )}
              </div>
            </div>

            <div style={{background:C.blueL,border:`1px solid ${C.blue}25`,borderRadius:12,padding:"12px 14px",marginBottom:18}}>
              <div style={{color:C.blue,fontWeight:700,fontSize:12,marginBottom:3}}>🔒 Privacidad</div>
              <div style={{color:C.sub,fontSize:12}}>Tu INE nunca se muestra públicamente. Solo lo usa el equipo Ficio para verificar tu identidad.</div>
            </div>

            {/* FIX: Btn movido dentro del fragmento <> antes del cierre </>. Estaba fuera causando "Adjacent JSX elements" */}
            <Btn full color={C.accent} onClick={()=>goStep(3)}>Continuar → Membresía</Btn>
            </>
            )}
          </div>
        )}

        {/* ══ PASO 3: MEMBRESÍA ════════════════════════════════════════════════ */}
        {step === 3 && (
          <div>

            {/* Encabezado del paso */}
            <div style={{marginBottom:6}}>
              <div style={{fontFamily:"Georgia,serif",fontSize:22,fontWeight:800,color:C.text,marginBottom:3}}>Elige tu plan y acepta las políticas</div>
              <div style={{color:C.muted,fontSize:12}}>Lee el reglamento completo. Sin aceptarlo no puedes registrarte.</div>
            </div>

            {/* 🔥 PÁGINA DE REGLAMENTO FICIO AGREGADA AQUÍ ══════════════════════ */}

            {/* ── BLOQUE 1: Periodo de prueba gratuito ───────────────────────── */}
            <div style={{background:C.greenL,border:`1.5px solid ${C.green}40`,borderRadius:16,padding:"16px 18px",marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <span style={{fontSize:22}}>🎁</span>
                <div>
                  <div style={{color:C.green,fontWeight:800,fontSize:14}}>Periodo de prueba gratuito</div>
                  <div style={{color:C.green,fontSize:11,opacity:0.85}}>Solo para los primeros 50 trabajadores registrados</div>
                </div>
              </div>
              {/* Badges */}
              <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
                {[
                  {icon:"📅",label:"60 días gratis"},
                  {icon:"👷",label:"Primeros 50"},
                  {icon:"✓",label:"Perfil visible"},
                ].map(b=>(
                  <div key={b.label} style={{background:"#fff",border:`1px solid ${C.green}40`,borderRadius:20,padding:"5px 12px",display:"flex",alignItems:"center",gap:5}}>
                    <span style={{fontSize:11}}>{b.icon}</span>
                    <span style={{color:C.green,fontWeight:700,fontSize:11}}>{b.label}</span>
                  </div>
                ))}
              </div>
              {/* Timeline */}
              <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:12}}>
                {[
                  {label:"Hoy",sub:"Te registras"},
                  {label:"60 días gratis",sub:null},
                  {label:"Día 61",sub:"Inicia cobro"},
                  {label:"Siempre",sub:"Membresía activa"},
                ].map((t,i,arr)=>(
                  <div key={t.label} style={{display:"flex",alignItems:"center",flex:i<arr.length-1?1:"auto"}}>
                    <div style={{textAlign:"center",minWidth:52}}>
                      <div style={{width:10,height:10,borderRadius:"50%",background:C.green,margin:"0 auto 3px"}}/>
                      <div style={{color:C.green,fontWeight:800,fontSize:9,whiteSpace:"nowrap"}}>{t.label}</div>
                      {t.sub && <div style={{color:C.muted,fontSize:8}}>{t.sub}</div>}
                    </div>
                    {i<arr.length-1 && <div style={{flex:1,height:2,background:`${C.green}40`,margin:"0 2px"}}/>}
                  </div>
                ))}
              </div>
              {/* Bullets ventajas */}
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                {[
                  "Tu perfil aparece visible en el directorio desde el primer día.",
                  "Los clientes pueden contactarte por WhatsApp desde el primer día.",
                  "A partir del día 61 se cobra la membresía según el plan que elijas.",
                  "Si no se registra el pago en la fecha límite el perfil se pausa automáticamente.",
                ].map((t,i)=>(
                  <div key={i} style={{display:"flex",gap:7,alignItems:"flex-start"}}>
                    <span style={{color:C.green,fontWeight:900,fontSize:12,marginTop:1}}>✓</span>
                    <span style={{color:C.text,fontSize:11,lineHeight:1.5}}>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── AVISO: Fondo NO activo durante prueba ───────────────────────── */}
            <div style={{background:"#FFF8ED",border:"1.5px solid #F5A623",borderRadius:14,padding:"14px 16px",marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <span style={{fontSize:16}}>⚠️</span>
                <span style={{color:"#B06000",fontWeight:800,fontSize:13}}>El Fondo de Contingencia NO está activo durante el periodo de prueba</span>
              </div>
              {[
                "Durante los 60 días de prueba gratuita NO cuentas con el apoyo del Fondo de Contingencia ante accidentes en obra.",
                "El fondo se activa únicamente cuando tu membresía mensual esté al corriente — a partir del día 61.",
                "Cualquier accidente ocurrido durante el periodo de prueba corre por cuenta del trabajador y el cliente — Ficio no interviene ni cubre ningún gasto médico en este periodo.",
                // FICIO: eliminado bullet de activación anticipada — sin pago en v1.
              ].map((t,i)=>(
                <div key={i} style={{display:"flex",gap:7,alignItems:"flex-start",marginBottom:5}}>
                  <span style={{color:"#B06000",fontSize:12,marginTop:1,flexShrink:0}}>·</span>
                  <span style={{color:"#7A4500",fontSize:11,lineHeight:1.5}}>{t}</span>
                </div>
              ))}
            </div>

            {/* ── BLOQUE 2: Plan preferido ─────────────────────────────────────── */}
            {/* FICIO v1: el plan es una preferencia; el admin lo activa manualmente por WhatsApp. */}
            <div style={{color:C.sub,fontSize:11,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",marginBottom:8}}>
              Elige tu plan preferido
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
              {PLANS.map(p => (
                <div key={p.id} onClick={()=>upd("plan",p.id)}
                  style={{background:form.plan===p.id?`${p.color}10`:C.surface,border:`2px solid ${form.plan===p.id?p.color:C.border}`,borderRadius:16,padding:"16px 18px",cursor:"pointer",display:"flex",gap:14,alignItems:"center",transition:"all 0.15s"}}>
                  <div style={{width:22,height:22,borderRadius:"50%",border:`2px solid ${form.plan===p.id?p.color:C.border}`,background:form.plan===p.id?p.color:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {form.plan===p.id && <div style={{width:8,height:8,borderRadius:"50%",background:"#fff"}}/>}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                      <span style={{fontFamily:"Georgia,serif",fontSize:16,fontWeight:800,color:C.text}}>{p.label}</span>
                      <span style={{fontFamily:"Georgia,serif",fontSize:18,fontWeight:900,color:p.color}}>{p.price}</span>
                    </div>
                    <div style={{color:C.muted,fontSize:12}}>{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── AVISO: Activación manual por WhatsApp ────────────────────────── */}
            {/* FICIO v1: sin pago en línea. El equipo contacta al trabajador para activar. */}
            <div style={{background:C.blueL,border:`1.5px solid ${C.blue}30`,borderRadius:14,padding:"14px 16px",marginBottom:20,display:"flex",gap:12,alignItems:"flex-start"}}>
              <span style={{fontSize:20,flexShrink:0}}>📲</span>
              <div>
                <div style={{color:C.blue,fontWeight:800,fontSize:13,marginBottom:4}}>Tu cuenta está en revisión</div>
                <div style={{color:C.sub,fontSize:12,lineHeight:1.6}}>
                  El equipo Ficio revisará tu INE y te contactará por WhatsApp en menos de 24 horas para activar tu membresía. No necesitas ingresar datos de pago ahora.
                </div>
              </div>
            </div>

            {/* ── BLOQUE 4: Políticas y Reglamento completo ──────────────────── */}
            <div style={{background:C.surface,border:`1.5px solid ${C.border}`,borderRadius:16,overflow:"hidden",marginBottom:14}}>
              {/* Encabezado */}
              <div style={{background:C.bg,borderBottom:`1px solid ${C.border}`,padding:"12px 16px",display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:16}}>📋</span>
                <span style={{color:C.text,fontWeight:800,fontSize:13}}>Políticas y Reglamento de Ficio</span>
                <span style={{background:C.redL,color:C.red,fontSize:9,fontWeight:900,borderRadius:6,padding:"2px 7px",marginLeft:"auto",whiteSpace:"nowrap"}}>OBLIGATORIO LEER</span>
              </div>
              {/* Cuerpo con scroll */}
              <div style={{maxHeight:300,overflowY:"auto",padding:"14px 16px",fontSize:11,color:C.sub,lineHeight:1.75}}>

                {/* Sección 1 */}
                <div style={{fontWeight:800,color:C.text,fontSize:12,marginBottom:6}}>1. Membresía y visibilidad</div>
                {[
                  "Tu perfil aparece en el directorio únicamente mientras tu membresía mensual esté al corriente.",
                  "Si no se registra el pago en la fecha de vencimiento tu perfil se pausa automáticamente sin previo aviso.",
                  "La reactivación del perfil ocurre en un plazo máximo de 24 horas después de confirmar el pago.",
                  "Ficio se reserva el derecho de suspender permanentemente perfiles con más de 2 meses de morosidad consecutiva.",
                ].map((t,i)=>(<div key={i} style={{display:"flex",gap:6,marginBottom:4}}><span style={{flexShrink:0,marginTop:1}}>·</span><span>{t}</span></div>))}

                {/* Sección 2 */}
                <div style={{fontWeight:800,color:C.text,fontSize:12,margin:"12px 0 6px"}}>2. Términos y condiciones del Fondo de Contingencia</div>
                <div style={{fontWeight:700,color:C.sub,fontSize:11,marginBottom:4}}>2.1 Naturaleza del fondo</div>
                {[
                  "El Fondo de Contingencia Ficio es un fondo colectivo autoadministrado — NO es un seguro de accidentes ni una póliza emitida por aseguradora.",
                  "Su propósito es brindar un apoyo económico de emergencia ante accidentes físicos ocurridos durante obra activa y registrada en Ficio.",
                  "El apoyo máximo es de $5,000 pesos por evento para plan Básico y $5,000 pesos para plan Premium. Este monto es primer auxilio — no cubre el costo total del accidente.",
                ].map((t,i)=>(<div key={i} style={{display:"flex",gap:6,marginBottom:4}}><span style={{flexShrink:0,marginTop:1}}>·</span><span>{t}</span></div>))}
                <div style={{fontWeight:700,color:C.sub,fontSize:11,margin:"8px 0 4px"}}>2.2 Requisitos de elegibilidad</div>
                {[
                  "Tener mínimo 30 días de membresía activa y pagada al momento del accidente.",
                  "El accidente debe ocurrir durante una obra activa confirmada por ambas partes dentro de Ficio.",
                  "El accidente debe ocurrir en la dirección declarada en la obra activa y dentro del horario registrado (Lunes a Sábado 7:00am – 6:00pm).",
                  "El trabajador no debe tener membresía vencida ni pagos pendientes al momento del accidente.",
                  "No haber agotado el límite de 2 reclamos por año calendario.",
                ].map((t,i)=>(<div key={i} style={{display:"flex",gap:6,marginBottom:4}}><span style={{flexShrink:0,marginTop:1}}>·</span><span>{t}</span></div>))}
                <div style={{fontWeight:700,color:C.sub,fontSize:11,margin:"8px 0 4px"}}>2.3 Documentación obligatoria</div>
                {[
                  "Ticket médico ORIGINAL de clínica u hospital registrado ante la Secretaría de Salud.",
                  "El gasto médico documentado debe ser de mínimo $500 pesos.",
                  "Fotografía del accidente tomada en el momento y en el sitio de la obra con metadatos de ubicación GPS activados.",
                  "Reporte del cliente confirmando dentro de Ficio que el accidente ocurrió durante la obra activa.",
                  "El reporte completo debe enviarse dentro de las primeras 48 horas desde el momento del accidente.",
                ].map((t,i)=>(<div key={i} style={{display:"flex",gap:6,marginBottom:4}}><span style={{flexShrink:0,marginTop:1}}>·</span><span>{t}</span></div>))}
                <div style={{fontWeight:700,color:C.sub,fontSize:11,margin:"8px 0 4px"}}>2.4 Exclusiones — el fondo NO cubre</div>
                {[
                  "Cortadas, rozaduras o laceraciones menores que no requirieron atención médica profesional.",
                  "Accidentes ocurridos fuera de la obra activa registrada en Ficio, incluyendo trayecto hacia o desde la obra.",
                  "Accidentes en obras no registradas o no confirmadas por ambas partes dentro de la plataforma.",
                  "Enfermedades, malestares, intoxicaciones o condiciones médicas preexistentes.",
                  "Accidentes ocurridos bajo el efecto de alcohol o sustancias.",
                  "Daños a materiales, herramientas o propiedad del cliente.",
                  "Accidentes reportados fuera del plazo de 48 horas.",
                  "Trabajadores en periodo de prueba gratuita de 60 días.",
                ].map((t,i)=>(<div key={i} style={{display:"flex",gap:6,marginBottom:4}}><span style={{flexShrink:0,marginTop:1,color:C.red}}>·</span><span>{t}</span></div>))}
                <div style={{fontWeight:700,color:C.sub,fontSize:11,margin:"8px 0 4px"}}>2.5 Proceso de resolución</div>
                {[
                  "Ficio revisa cada solicitud manualmente. No existe pago automático del fondo.",
                  "El tiempo de resolución es de máximo 72 horas después de recibir la documentación completa y verificada.",
                  "La resolución de Ficio es definitiva. No existe proceso de apelación salvo nueva evidencia presentada en 5 días.",
                  "El pago del apoyo se realiza al número de cuenta o Mercado Pago registrado en el perfil del trabajador.",
                ].map((t,i)=>(<div key={i} style={{display:"flex",gap:6,marginBottom:4}}><span style={{flexShrink:0,marginTop:1}}>·</span><span>{t}</span></div>))}
                <div style={{fontWeight:700,color:C.sub,fontSize:11,margin:"8px 0 4px"}}>2.6 Sanciones por uso fraudulento</div>
                {[
                  "Presentar documentación falsa o alterada resulta en suspensión permanente e inmediata sin derecho a reembolso.",
                  "Ficio puede denunciar ante autoridades competentes casos de fraude comprobado al fondo colectivo.",
                  "Un trabajador con más de 2 reclamos en 12 meses queda bajo observación automática.",
                ].map((t,i)=>(<div key={i} style={{display:"flex",gap:6,marginBottom:4}}><span style={{flexShrink:0,marginTop:1}}>·</span><span>{t}</span></div>))}

                {/* Sección 3 */}
                <div style={{fontWeight:800,color:C.text,fontSize:12,margin:"12px 0 6px"}}>3. Reputación y calificaciones</div>
                {[
                  "Las calificaciones que recibes de clientes son permanentes y públicas — no pueden eliminarse salvo en casos de abuso comprobado.",
                  "Solo los clientes que realizaron una obra activa contigo en Ficio pueden calificarte.",
                  "Un promedio menor a 2.0 estrellas puede resultar en la suspensión temporal de tu perfil.",
                ].map((t,i)=>(<div key={i} style={{display:"flex",gap:6,marginBottom:4}}><span style={{flexShrink:0,marginTop:1}}>·</span><span>{t}</span></div>))}

                {/* Sección 4 */}
                <div style={{fontWeight:800,color:C.text,fontSize:12,margin:"12px 0 6px"}}>4. Gestión de obra y mediación</div>
                {[
                  "La obra debe ser iniciada y cerrada por el cliente dentro de Ficio para que la cobertura del fondo sea válida.",
                  "Si el cliente no cierra la obra en un plazo de 7 días después de completado el trabajo, Ficio la cierra automáticamente.",
                  "El trabajador puede solicitar mediación a través de un supervisor de Ficio. El costo es $499 pesos la visita.",
                  "Ficio actúa como mediador imparcial — la resolución del supervisor designado es definitiva y no apelable.",
                ].map((t,i)=>(<div key={i} style={{display:"flex",gap:6,marginBottom:4}}><span style={{flexShrink:0,marginTop:1}}>·</span><span>{t}</span></div>))}

                {/* Sección 5 */}
                <div style={{fontWeight:800,color:C.text,fontSize:12,margin:"12px 0 6px"}}>5. Causas de suspensión permanente</div>
                {[
                  "Compartir datos de contacto con clientes para evadir la plataforma.",
                  "Falsificar documentos de identidad o información del perfil.",
                  "Acumular 3 o más reportes verificados de incumplimiento de trabajos.",
                  "Conducta abusiva, amenazante o fraudulenta hacia clientes, supervisores o el equipo de Ficio.",
                  "Manipulación de calificaciones o reseñas propias o ajenas.",
                ].map((t,i)=>(<div key={i} style={{display:"flex",gap:6,marginBottom:4}}><span style={{flexShrink:0,marginTop:1,color:C.red}}>·</span><span>{t}</span></div>))}

              </div>{/* fin scroll */}
            </div>

            {/* ── BLOQUE 5: Garantías ─────────────────────────────────────────── */}
            <div style={{background:C.blueL,border:`1.5px solid ${C.blue}30`,borderRadius:14,padding:"14px 16px",marginBottom:18}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <span style={{fontSize:16}}>🛡️</span>
                <span style={{color:C.blue,fontWeight:800,fontSize:13}}>Garantías que Ficio te ofrece</span>
              </div>
              {[
                ["✓","Visibilidad garantizada","Mientras tu pago esté al corriente tu perfil aparece en el directorio sin excepción."],
                ["✓","Respaldo ante accidentes en obra","Apoyo de emergencia de hasta $5,000 pesos para primeros gastos médicos. No es cobertura total del costo real del accidente."],
                ["✓","Reputación protegida","Solo clientes reales que trabajaron contigo pueden calificarte."],
                ["✓","Mediación justa","Ante disputas con clientes tienes derecho a un proceso de mediación con supervisor certificado."],
                ["✓","Datos seguros","Tu CURP, INE y datos personales nunca se comparten públicamente ni con terceros."],
              ].map(([icon,title,desc])=>(
                <div key={title} style={{display:"flex",gap:10,marginBottom:9,alignItems:"flex-start"}}>
                  <div style={{width:20,height:20,borderRadius:6,background:C.blue,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                    <span style={{color:"#fff",fontSize:11,fontWeight:900}}>{icon}</span>
                  </div>
                  <div>
                    <div style={{color:C.blue,fontWeight:700,fontSize:12,marginBottom:1}}>{title}</div>
                    <div style={{color:C.sub,fontSize:11,lineHeight:1.5}}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── BLOQUE 6: Checkboxes de aceptación ─────────────────────────── */}
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:18}}>
              {[
                ["acepta_politicas",   "He leído y acepto el reglamento completo de Ficio, incluyendo las causas de suspensión permanente."],
                ["acepta_fondo",       "Entiendo el funcionamiento del Fondo de Contingencia, sus límites de cobertura y los requisitos para acceder a él."],
                ["acepta_visibilidad", "Acepto que mi visibilidad en el directorio depende de mantener mi membresía mensual al corriente."],
              ].map(([key, texto]) => (
                <div key={key} onClick={()=>upd(key,!form[key])}
                  style={{display:"flex",gap:12,alignItems:"flex-start",cursor:"pointer",background:form[key]?C.greenL:C.surface,border:`1.5px solid ${form[key]?C.green:C.border}`,borderRadius:12,padding:"12px 14px",transition:"all 0.15s"}}>
                  <div style={{width:22,height:22,borderRadius:6,border:`2px solid ${form[key]?C.green:C.border}`,background:form[key]?C.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                    {form[key] && <span style={{color:"#fff",fontSize:13,fontWeight:800}}>✓</span>}
                  </div>
                  <span style={{color:form[key]?C.green:C.sub,fontSize:12,lineHeight:1.5}}>{texto}</span>
                </div>
              ))}
            </div>
            {/* 🔥 FIN PÁGINA DE REGLAMENTO FICIO ════════════════════════════════ */}

            {/* ── Resumen final ───────────────────────────────────────────────── */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:"14px 16px",marginBottom:18,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
              <div style={{color:C.text,fontSize:13,fontWeight:700,marginBottom:10}}>Resumen de tu registro</div>
              {[
                ["Nombre",    form.name  || "—"],
                ["Oficio",    form.trade],
                ["Zona",      form.zone],
                ["Plan",      PLANS.find(p=>p.id===form.plan)?.label || "—"],
                ["Periodo de prueba", "60 días gratis · fondo activo día 61"],
                ["Estado",    "En revisión · te contactamos por WhatsApp"],
              ].map(([k,v]) => (
                <div key={k} style={{display:"flex",justifyContent:"space-between",marginBottom:5,paddingBottom:5,borderBottom:`1px solid ${C.divider}`}}>
                  <span style={{color:C.muted,fontSize:12}}>{k}</span>
                  <span style={{fontSize:12,fontWeight:700,color:C.text}}>{v}</span>
                </div>
              ))}
            </div>

            {errorMsg && (
              <div style={{background:C.redL,border:`1px solid ${C.red}30`,borderRadius:12,padding:"10px 12px",color:C.red,fontSize:12,marginBottom:14}}>
                {errorMsg}
              </div>
            )}

            {saving && uploadProgress && (
              <div style={{background:C.accentL,border:`1px solid ${C.accent}30`,borderRadius:12,padding:"10px 14px",marginBottom:14,color:C.accent,fontSize:13,fontWeight:700,textAlign:"center"}}>
                ⏳ {uploadProgress}
              </div>
            )}

            <Btn
              full
              color={form.acepta_politicas && form.acepta_fondo && form.acepta_visibilidad ? C.accent : "#CCC"}
              onClick={guardarTrabajador}
            >
              {saving ? "Guardando..." : "Registrarme en Ficio ✓"}
            </Btn>

            {!(form.acepta_politicas && form.acepta_fondo && form.acepta_visibilidad) && (
              <div style={{color:C.muted,fontSize:11,textAlign:"center",marginTop:8}}>
                Debes aceptar las tres políticas para completar tu registro
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ─── EDITAR PERFIL MAESTRO ────────────────────────────────────────────────────
export const EditarPerfilMaestro = ({setScreen, user, refreshDirectory}) => {
  const PORTFOLIO_LABELS = ["Trabajo terminado","Detalle de obra","Antes y después","Material usado","En proceso","Vista general"];

  const [saving,        setSaving]        = useState(false);
  const [loading,       setLoading]       = useState(true);
  const [errorMsg,      setErrorMsg]      = useState("");
  const [successMsg,    setSuccessMsg]    = useState("");
  const [uploadProgress,setUploadProgress]= useState("");

  const [form, setForm] = useState({
    name:"", phone:"", trade:"Albañil", specialty:"", zone:"Los Mochis",
    exp:"", descripcion:"",
    photo:null, photo_url_existente:"",
    ine:null,   ine_url_existente:"",
    portfolioPhotos:[],
  });
  const upd = (k,v) => { setErrorMsg(""); setSuccessMsg(""); setForm(f=>({...f,[k]:v})); };

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    // Reset form to blank state before every fetch — never carry over values from a previous session.
    setForm({
      name:"", phone:"", trade:"Albañil", specialty:"", zone:"Los Mochis",
      exp:"", descripcion:"",
      photo:null, photo_url_existente:"",
      ine:null,   ine_url_existente:"",
      portfolioPhotos:[],
    });
    setErrorMsg("");
    setLoading(true);
    (async () => {
      try {
        const { data: authCheck } = await supabase.auth.getUser();
        if (!authCheck?.user?.id || authCheck.user.id !== user.id) {
          setErrorMsg("Error de sesión. Cierra sesión e inicia de nuevo.");
          setLoading(false);
          return;
        }
        // Fetch perfiles (shared identity) and trabajadores (worker fields) in parallel.
        // nombre/telefono/correo come ONLY from perfiles — never from a trabajadores row.
        const [wRes, pRes] = await Promise.all([
          supabase.from("trabajadores").select("*").eq("user_id", user.id).neq("archivado", true).maybeSingle(),
          supabase.from("perfiles").select("nombre,telefono,correo,foto_url,ine_url").eq("user_id", user.id).neq("archivado", true).maybeSingle(),
        ]);
        if (wRes.error) throw wRes.error;
        const w = wRes.data;
        const p = pRes.data;

        // Safety guard: if the returned row belongs to a different user, block and reset.
        if (w?.user_id && w.user_id !== user.id) {
          console.warn("[EditarPerfilMaestro] Blocked mismatched user data — got user_id:", w.user_id, "expected:", user.id);
          setLoading(false);
          return;
        }
        if (p?.user_id && p.user_id !== user.id) {
          console.warn("[EditarPerfilMaestro] Blocked mismatched perfil data — got user_id:", p.user_id, "expected:", user.id);
          setLoading(false);
          return;
        }

        if (!w && !p) { setLoading(false); return; }

        // Shared identity from perfiles only — never from user_metadata.
        const nombreFuente   = p?.nombre   || "";
        const telefonoFuente = String(p?.telefono || "").replace(/\D/g, "");

        const fotoUrl = w?.foto_url || w?.avatar || p?.foto_url || "";
        const ineUrl  = w?.ine_url  || p?.ine_url || "";
        const existingPortfolio = Array.isArray(w?.portfolio_urls)
          ? w.portfolio_urls.slice(0, 6).map((url, i) =>
              url ? { label: PORTFOLIO_LABELS[i] || `Foto ${i+1}`, uploaded: true, file: null, preview: url } : null
            )
          : [];

        // Full replacement — no spread of previous form state.
        setForm({
          name:                nombreFuente,
          phone:               telefonoFuente,
          trade:               w?.oficio        || "Albañil",
          specialty:           w?.especialidad  || "",
          zone:                w?.zona          || "Los Mochis",
          exp:                 String(w?.exp    || ""),
          descripcion:         w?.descripcion   || "",
          photo:               fotoUrl || null,
          photo_url_existente: fotoUrl,
          ine:                 ineUrl  || null,
          ine_url_existente:   ineUrl,
          portfolioPhotos:     existingPortfolio,
        });
      } catch(e) {
        setErrorMsg("Error cargando perfil: " + (e?.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]); // eslint-disable-line

  const guardar = async () => {
    if (!form.name.trim())  return setErrorMsg("Escribe tu nombre completo.");
    if (!form.phone.trim()) return setErrorMsg("Escribe tu número de WhatsApp.");
    if (!form.trade)        return setErrorMsg("Selecciona tu oficio principal.");
    setSaving(true); setErrorMsg(""); setSuccessMsg("");
    try {
      const { data: authCheck } = await supabase.auth.getUser();
      if (!authCheck?.user?.id || authCheck.user.id !== user.id) {
        throw new Error("Error de sesión. Cierra sesión e inicia de nuevo.");
      }

      let fotoUrl = typeof form.photo === "string" ? form.photo : "";
      let ineUrl  = typeof form.ine   === "string" ? form.ine   : "";
      const portfolioUrls = [];

      // FIX F3-16: validar tipo y tamaño antes de subir.
      const photoErrE = validateImageFile(form.photo instanceof File ? form.photo : null, "Foto");
      const ineErrE   = validateImageFile(form.ine   instanceof File ? form.ine   : null, "INE");
      if (photoErrE) throw new Error(photoErrE);
      if (ineErrE)   throw new Error(ineErrE);

      setUploadProgress("Subiendo foto de perfil...");
      if (form.photo instanceof File) {
        const url = await uploadPublicFile(form.photo, "fotos-perfil", authCheck.user.id + "-perfil");
        if (url) fotoUrl = url;
      }

      setUploadProgress("Subiendo INE...");
      if (form.ine instanceof File) {
        const url = await uploadPublicFile(form.ine, "documentos-identidad", authCheck.user.id + "-ine");
        if (url) ineUrl = url;
      }

      setUploadProgress("Subiendo portafolio...");
      for (const [i, p] of (form.portfolioPhotos || []).entries()) {
        if (!p) { portfolioUrls.push(null); continue; }
        if (p.file instanceof File) {
          const url = await uploadPublicFile(p.file, "ficio-media", authCheck.user.id + "-portfolio-" + i);
          portfolioUrls.push(url || null);
        } else {
          portfolioUrls.push(p.preview || null);
        }
      }

      setUploadProgress("Guardando perfil...");
      const confirmedUserId = authCheck.user.id;
      const cleanPhone = form.phone.replace(/\D/g,"");
      const cleanName  = form.name.trim();

      // Step 1: Save shared identity to perfiles FIRST — this is the authoritative source.
      const perfilSync = Object.fromEntries(
        Object.entries({
          user_id:    confirmedUserId,
          nombre:     cleanName  || undefined,
          telefono:   cleanPhone || undefined,
          foto_url:   fotoUrl    || undefined,
          avatar_url: fotoUrl    || undefined,
          ine_url:    ineUrl     || undefined,
          updated_at: new Date().toISOString(),
        }).filter(([, v]) => v !== undefined)
      );
      await supabase.from("perfiles").upsert([perfilSync], { onConflict: "user_id" });

      // Step 2: Re-fetch perfiles to get the confirmed nombre/telefono (defensive).
      const { data: confirmedPerfil } = await supabase
        .from("perfiles")
        .select("nombre,telefono")
        .eq("user_id", confirmedUserId)
        .neq("archivado", true)
        .maybeSingle();

      // Step 3: Rule 7 — if the DB value diverges from what we just wrote (race condition),
      // use the DB value and log. Under normal flow they always match.
      let nombreParaTrabajador   = cleanName;
      let telefonoParaTrabajador = cleanPhone;
      if (confirmedPerfil?.nombre && confirmedPerfil.nombre !== cleanName) {
        console.warn("[EditarPerfilMaestro] nombre mismatch — form:", cleanName, "perfil DB:", confirmedPerfil.nombre, "— usando valor de perfil");
        nombreParaTrabajador   = confirmedPerfil.nombre;
        telefonoParaTrabajador = String(confirmedPerfil.telefono || cleanPhone).replace(/\D/g, "");
      }

      // Step 4: Build trabajadores payload using confirmed shared-identity values.
      const payload = {
        nombre:         nombreParaTrabajador,
        telefono:       telefonoParaTrabajador,
        whatsapp:       telefonoParaTrabajador,
        oficio:         form.trade,
        especialidad:   form.specialty || "",
        descripcion:    form.descripcion || "",
        zona:           form.zone,
        ciudad:         form.zone,
        exp:            Number(form.exp) || 0,
        experiencia:    String(form.exp || 0),
        foto_url:       fotoUrl,
        ine_url:        ineUrl,
        portfolio_urls: portfolioUrls.filter(Boolean),
        updated_at:     new Date().toISOString(),
      };

      const upsertPayload = Object.fromEntries(
        Object.entries({ ...payload, user_id: confirmedUserId }).filter(([, v]) => v !== null && v !== undefined)
      );
      const { error } = await supabase
        .from("trabajadores")
        .upsert([upsertPayload], { onConflict: "user_id" });
      if (error) throw error;

      upsertDirectoryWorkerCache({ ...payload, user_id: confirmedUserId });
      if (typeof refreshDirectory === "function") await refreshDirectory({ force: true });

      setSuccessMsg("¡Perfil actualizado correctamente!");
      setTimeout(() => setScreen("cuenta"), 1500);
    } catch(e) {
      setErrorMsg(e?.message || "Error al guardar. Intenta de nuevo.");
    } finally {
      setSaving(false);
      setUploadProgress("");
    }
  };

  if (loading) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center",color:C.muted}}>
        <div style={{fontSize:36,marginBottom:10}}>⏳</div>
        <div style={{fontSize:14}}>Cargando tu perfil...</div>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:C.bg,paddingBottom:80}}>
      <TopNav setScreen={setScreen} back="cuenta" backLabel="Cuenta" title="Editar perfil maestro"/>
      <div style={{maxWidth:560,margin:"0 auto",padding:"24px 20px"}}>

        {errorMsg && (
          <div style={{background:C.redL,border:`1px solid ${C.red}30`,borderRadius:12,padding:"10px 14px",color:C.red,fontSize:13,fontWeight:700,marginBottom:16}}>
            ⚠️ {errorMsg}
          </div>
        )}
        {successMsg && (
          <div style={{background:C.greenL,border:`1px solid ${C.green}30`,borderRadius:12,padding:"10px 14px",color:C.green,fontSize:13,fontWeight:700,marginBottom:16}}>
            ✅ {successMsg}
          </div>
        )}
        {uploadProgress && (
          <div style={{background:C.blueL,border:`1px solid ${C.blue}25`,borderRadius:12,padding:"10px 14px",color:C.blue,fontSize:13,marginBottom:16}}>
            ⏳ {uploadProgress}
          </div>
        )}

        {/* ── Datos básicos ─────────────────────────────────────────────────── */}
        <div style={{fontFamily:"Georgia,serif",fontSize:18,fontWeight:800,color:C.text,marginBottom:16}}>Datos del perfil</div>

        <Inp label="Nombre completo" placeholder="Como aparece en tu INE" value={form.name} onChange={e=>upd("name",e.target.value)} icon="👤"/>
        <Inp label="WhatsApp" type="tel" placeholder="10 dígitos" value={form.phone} onChange={e=>upd("phone",e.target.value)} icon="📱"/>
        <Sel label="Oficio principal" value={form.trade} onChange={e=>upd("trade",e.target.value)} options={ALL_TRADES}/>
        <Inp label="Especialidad" placeholder="Ej: Cimentaciones, azulejo, interiores..." value={form.specialty} onChange={e=>upd("specialty",e.target.value)} icon="🔧"/>
        <Sel label="Ciudad donde trabajas" value={form.zone} onChange={e=>upd("zone",e.target.value)} options={["Los Mochis","Culiacán","Mazatlán","Guasave","Guamúchil","El Fuerte","Ahome","Navolato"]}/>
        <Inp label="Años de experiencia" type="number" placeholder="Ej: 8" value={form.exp} onChange={e=>upd("exp",e.target.value)} icon="📅"/>
        <Inp label="Descripción / Bio" placeholder="Cuéntale a los clientes quién eres y qué haces..." value={form.descripcion} onChange={e=>upd("descripcion",e.target.value)} icon="📝"/>

        {/* ── Foto de perfil ────────────────────────────────────────────────── */}
        <div style={{fontFamily:"Georgia,serif",fontSize:18,fontWeight:800,color:C.text,marginBottom:12,marginTop:8}}>Foto de perfil</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
          <div>
            {form.photo_url_existente && typeof form.photo === "string" ? (
              <div style={{border:`2px solid ${C.green}`,borderRadius:16,padding:14,textAlign:"center",background:C.greenL}}>
                {form.photo_url_existente.startsWith("http") && (
                  <img src={form.photo_url_existente} alt="Perfil" style={{width:56,height:56,borderRadius:"50%",objectFit:"cover",marginBottom:6,display:"block",margin:"0 auto 6px"}}/>
                )}
                <div style={{color:C.green,fontWeight:700,fontSize:12}}>Foto actual</div>
                <button onClick={()=>upd("photo",null)} style={{background:"none",border:"none",color:C.accent,fontSize:11,cursor:"pointer",marginTop:6,fontWeight:700}}>Cambiar foto</button>
              </div>
            ) : (
              <FileUploadCard id="editar-photo" value={form.photo} onChange={file=>upd("photo",file)} icon="📸" title="Foto de perfil" sub="Cara visible — recomendado"/>
            )}
          </div>
          <div>
            {form.ine_url_existente && typeof form.ine === "string" ? (
              <div style={{border:`2px solid ${C.green}`,borderRadius:16,padding:14,textAlign:"center",background:C.greenL}}>
                <div style={{fontSize:28,marginBottom:4}}>✅</div>
                <div style={{color:C.green,fontWeight:700,fontSize:12}}>INE guardado</div>
                <div style={{color:C.muted,fontSize:10,marginTop:2}}>Verificado por Ficio</div>
                <button onClick={()=>upd("ine",null)} style={{background:"none",border:"none",color:C.accent,fontSize:11,cursor:"pointer",marginTop:6,fontWeight:700}}>Cambiar INE</button>
              </div>
            ) : (
              <FileUploadCard id="editar-ine" value={form.ine} onChange={file=>upd("ine",file)} icon="🪪" title="Foto de INE" sub="Frente visible"/>
            )}
          </div>
        </div>

        {/* ── Portafolio ────────────────────────────────────────────────────── */}
        <div style={{fontFamily:"Georgia,serif",fontSize:18,fontWeight:800,color:C.text,marginBottom:8}}>Fotos de trabajos</div>
        <div style={{background:C.accentL,border:`1px solid ${C.accent}25`,borderRadius:10,padding:"10px 14px",marginBottom:12,display:"flex",gap:8,alignItems:"center"}}>
          <span style={{fontSize:16}}>💡</span>
          <div style={{color:C.accent,fontSize:12,lineHeight:1.5}}>Máximo 6 fotos. Muestra trabajos reales — esto genera más confianza en los clientes.</div>
        </div>
        <PhotoGrid form={form} upd={upd}/>
        <div style={{color:C.muted,fontSize:11,textAlign:"right",marginTop:4,marginBottom:20}}>
          {(form.portfolioPhotos||[]).filter(Boolean).length}/6 fotos
        </div>

        <Btn full color={C.accent} onClick={guardar} disabled={saving}>
          {saving ? (uploadProgress || "Guardando...") : "Guardar cambios ✓"}
        </Btn>

      </div>
    </div>
  );
};